import React from 'react';
import { resolveIcon } from './resolveIcon';
import { resolveTablerIconComponent } from './tablerIconMap';

const STROKE = 2;

/**
 * @param {object} props
 * @param {string} props.iconKey
 * @param {'squircle'|'line'} [props.variant]
 * @param {'xs'|'sm'|'md'|'lg'} [props.size]
 * @param {string} [props.bgToken]
 * @param {boolean} [props.active] squircle gradient when true (e.g. tab active)
 * @param {string} [props.className]
 * @param {string} [props.ariaLabel]
 */
export default function MingoIcon({
  iconKey,
  variant = 'squircle',
  size = 'md',
  bgToken,
  active = false,
  className = '',
  ariaLabel,
}) {
  const resolved = resolveIcon(iconKey, variant, { bgToken, size, active, context: 'MingoIcon' });
  const px = resolved.sizePx;
  const isLine = variant === 'line';
  const decorative = !ariaLabel;
  const TablerIcon = resolved.tabler ? resolveTablerIconComponent(resolved.tabler) : null;

  const iconFg = resolved.iconColor || (isLine ? 'currentColor' : 'var(--icon-fg-on-bg)');
  const iconStroke = isLine ? STROKE : 1.75;

  const svg = TablerIcon ? (
    <TablerIcon
      size={Math.round(px * 0.62)}
      stroke={iconStroke}
      color={iconFg}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : ariaLabel}
      className="mingo-icon__svg"
    />
  ) : (
    <svg
      width={px}
      height={px}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : ariaLabel}
      className="mingo-icon__svg"
    >
      {resolved.paths.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke={iconFg}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      ))}
    </svg>
  );

  if (isLine) {
    return (
      <span
        className={`mingo-icon mingo-icon--line mingo-icon--${size} ${className}`.trim()}
        style={{ width: px, height: px, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {svg}
      </span>
    );
  }

  const bgStyle = resolved.iconBg
    ? { background: resolved.iconBg }
    : resolved.useGradient
      ? { background: 'var(--icon-grad-brand)' }
      : { background: `var(${resolved.bgVar})` };

  return (
    <span
      className={`mingo-icon mingo-icon--squircle mingo-icon--${size} ${className}`.trim()}
      style={{
        width: px,
        height: px,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...bgStyle,
      }}
    >
      {svg}
    </span>
  );
}
